import { BillingState } from "../../../types/billing";
import { PaymentMethod, PaymentHistory } from "../../../interfaces/billing";
import { actionTypes } from "./billingActionTypes";

export { actionTypes };

interface ExtendedBillingState extends BillingState {
  paymentMethods: PaymentMethod[];
  paymentHistory: PaymentHistory[];
  setupIntentSecret: string | null;
  paymentMethodsLoading: boolean;
  historyLoading: boolean;
  customerInfo: { name: string; email: string } | null;
  customerLoading: boolean;
  selectedInvoice: any | null;
  invoiceLoading: boolean;
  currentPlan: any | null;
  paymentProcessing: boolean;
  donationCancelLoading: boolean;
}

const idleAccountAction = { status: "idle", message: null, fieldErrors: {}, code: null } as const;

const initialState: ExtendedBillingState = {
  plans: [],
  subscription: null,
  donation: null,
  donationCancelLoading: false,
  oneTimePurchase: null,
  subscriptionSettled: false,
  hasActiveSubscription: false,
  invoices: [],
  loading: false,
  error: null,
  paymentMethods: [],
  paymentHistory: [],
  setupIntentSecret: null,
  paymentMethodsLoading: false,
  historyLoading: false,
  customerInfo: null,
  customerLoading: false,
  selectedInvoice: null,
  invoiceLoading: false,
  currentPlan: null,
  paymentProcessing: false,
  awaitingPaymentConfirmation: false,
  // Trial / onboarding fields (populated from /api/auth/me billing payload)
  trialDays: 30,
  trialEndsAt: null,
  isTrialActive: false,
  trialRemainingHours: 0,
  welcomeSplashSeen: true, // Default true to prevent flash before auth resolves
  isNewUser: false,
  contactCount: 0,
  contactLimit: null,
  recommendedTier: null,
  overContactLimit: false,
  contactLimitOver: false,
  contactLimitGraceUntil: null,
  managedUploadRequested: false,
  managedUploadFileUploaded: false,
  managedUploadStatus: null,
  managedUploadAdminMessage: null,
  isSuperAdminWorkspace: false,
  billingEnabled: false,
  subscriptionStatus: null,
  adminContact: null,
  isWorkspaceOwner: false,
  accountState: "active",
  closesAt: null,
  deleteAfter: null,
  accountAction: idleAccountAction,
};

export const billingReducer = (state = initialState, action: any): ExtendedBillingState => {
  switch (action.type) {
    case actionTypes.CANCEL_SUBSCRIPTION:
    case actionTypes.CLOSE_ACCOUNT:
    case actionTypes.KEEP_ACCOUNT:
      return { ...state, loading: true, error: null, accountAction: { ...idleAccountAction, status: "loading" } };

    case actionTypes.ACCOUNT_ACTION_SUCCESS:
      return { ...state, loading: false, accountAction: { ...idleAccountAction, status: "success" } };

    case actionTypes.ACCOUNT_ACTION_ERROR:
      return {
        ...state,
        loading: false,
        accountAction: {
          status: "error",
          message: action.payload?.message ?? null,
          fieldErrors: action.payload?.fieldErrors ?? {},
          code: action.payload?.code ?? null,
        },
      };

    case actionTypes.ACCOUNT_ACTION_RESET:
      return { ...state, accountAction: idleAccountAction };

    case actionTypes.GET_PLANS:
    case actionTypes.GET_SUBSCRIPTION:
    case actionTypes.GET_INVOICES:
    case actionTypes.CREATE_PORTAL_SESSION:
    case actionTypes.RESUME_SUBSCRIPTION:
      return { ...state, loading: true, error: null };

    case actionTypes.CREATE_CHECKOUT_SESSION:
      return { ...state, loading: true, error: null, paymentProcessing: true };

    case actionTypes.PURCHASE_SETUP_FEE_TRIGGER:
      return { ...state, paymentProcessing: true };

    case actionTypes.GET_PLANS_SUCCESS:
      return { ...state, loading: false, plans: action.payload };

    case actionTypes.GET_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        loading: false,
        subscriptionSettled: true,
        subscription: action.payload.subscription,
        // Top-level `donation` on the response — present even when there's no
        // `subscriptions` row at all. Falls back to subscription.donation for
        // callers (resume/subscribe/change-plan responses) that only nest it.
        donation: action.payload.donation ?? action.payload.subscription?.donation ?? null,
        oneTimePurchase: action.payload.one_time_purchase,
        // Older backends omit the account fields: treat that as a normal, open account.
        accountState: action.payload.account_state ?? "active",
        closesAt: action.payload.closes_at ?? null,
        deleteAfter: action.payload.delete_after ?? null,
        hasActiveSubscription: action.payload.has_active_subscription,
        // If subscription is now active, payment confirmation is complete
        awaitingPaymentConfirmation: action.payload.has_active_subscription
          ? false
          : state.awaitingPaymentConfirmation,
      };

    case actionTypes.CANCEL_DONATION:
      return { ...state, donationCancelLoading: true };

    case actionTypes.CANCEL_DONATION_SUCCESS:
      return { ...state, donationCancelLoading: false, donation: action.payload.donation };

    case actionTypes.CANCEL_DONATION_ERROR:
      return { ...state, donationCancelLoading: false };

    case actionTypes.CREATE_CHECKOUT_SESSION_SUCCESS:
    case actionTypes.CREATE_PORTAL_SESSION_SUCCESS:
      return { ...state, loading: false, paymentProcessing: false };

    case actionTypes.SET_PAYMENT_PROCESSING:
      return { ...state, paymentProcessing: action.payload };

    case actionTypes.SET_AWAITING_PAYMENT_CONFIRMATION:
      return { ...state, awaitingPaymentConfirmation: action.payload };

    case actionTypes.GET_INVOICES_SUCCESS:
      return { ...state, loading: false, invoices: action.payload };

    case actionTypes.GET_PLANS_ERROR:
    case actionTypes.GET_SUBSCRIPTION_ERROR:
    case actionTypes.GET_INVOICES_ERROR:
    case actionTypes.CREATE_PORTAL_SESSION_ERROR:
    case actionTypes.CANCEL_SUBSCRIPTION_ERROR:
    case actionTypes.RESUME_SUBSCRIPTION_ERROR:
      return { ...state, loading: false, error: action.payload, ...(action.type === actionTypes.GET_SUBSCRIPTION_ERROR ? { subscriptionSettled: true } : {}) };

    case actionTypes.CREATE_CHECKOUT_SESSION_ERROR:
      return { ...state, loading: false, error: action.payload, paymentProcessing: false };

    case actionTypes.CLEAR_BILLING_ERROR:
      return { ...state, error: null };

    // Payment Methods
    case actionTypes.GET_PAYMENT_METHODS:
    case actionTypes.ADD_PAYMENT_METHOD:
    case actionTypes.DELETE_PAYMENT_METHOD:
    case actionTypes.SET_DEFAULT_PAYMENT_METHOD:
    case actionTypes.GET_SETUP_INTENT:
      return { ...state, paymentMethodsLoading: true, error: null };

    case actionTypes.GET_PAYMENT_METHODS_SUCCESS:
      return { ...state, paymentMethodsLoading: false, paymentMethods: action.payload };

    case actionTypes.ADD_PAYMENT_METHOD_SUCCESS:
      return {
        ...state,
        paymentMethodsLoading: false,
        paymentMethods: [...state.paymentMethods, action.payload],
        setupIntentSecret: null,
      };

    case actionTypes.DELETE_PAYMENT_METHOD_SUCCESS:
      return {
        ...state,
        paymentMethodsLoading: false,
        paymentMethods: state.paymentMethods.filter(pm => pm.id !== action.payload)
      };

    case actionTypes.SET_DEFAULT_PAYMENT_METHOD_SUCCESS:
      return {
        ...state,
        paymentMethodsLoading: false,
        paymentMethods: state.paymentMethods.map(pm => ({
          ...pm,
          is_default: pm.id === action.payload
        }))
      };

    case actionTypes.GET_SETUP_INTENT_SUCCESS:
      return { ...state, paymentMethodsLoading: false, setupIntentSecret: action.payload };

    case actionTypes.GET_PAYMENT_METHODS_ERROR:
    case actionTypes.ADD_PAYMENT_METHOD_ERROR:
    case actionTypes.DELETE_PAYMENT_METHOD_ERROR:
    case actionTypes.SET_DEFAULT_PAYMENT_METHOD_ERROR:
    case actionTypes.GET_SETUP_INTENT_ERROR:
      return { ...state, paymentMethodsLoading: false, error: action.payload };

    // Payment History
    case actionTypes.GET_PAYMENT_HISTORY:
      return { ...state, historyLoading: true, error: null };

    case actionTypes.GET_PAYMENT_HISTORY_SUCCESS:
      return { ...state, historyLoading: false, paymentHistory: action.payload };

    case actionTypes.GET_PAYMENT_HISTORY_ERROR:
      return { ...state, historyLoading: false, error: action.payload };

    // Customer Info
    case actionTypes.GET_CUSTOMER:
    case actionTypes.UPDATE_CUSTOMER:
      return { ...state, customerLoading: true, error: null };

    case actionTypes.GET_CUSTOMER_SUCCESS:
    case actionTypes.UPDATE_CUSTOMER_SUCCESS:
      return { ...state, customerLoading: false, customerInfo: action.payload };

    case actionTypes.GET_CUSTOMER_ERROR:
    case actionTypes.UPDATE_CUSTOMER_ERROR:
      return { ...state, customerLoading: false, error: action.payload };

    case actionTypes.GET_INVOICE:
      return { ...state, invoiceLoading: true, selectedInvoice: null, error: null };

    case actionTypes.GET_INVOICE_SUCCESS:
      return { ...state, invoiceLoading: false, selectedInvoice: action.payload };

    case actionTypes.GET_INVOICE_ERROR:
      return { ...state, invoiceLoading: false, error: action.payload };

    case actionTypes.SET_BILLING_FROM_AUTH:
      return {
        ...state,
        ...action.payload,
        // If auth confirms an active subscription or managedUploadRequested, clear the
        // awaiting confirmation flag so the beforeunload warning is dismissed.
        awaitingPaymentConfirmation:
          (action.payload.hasActiveSubscription || action.payload.managedUploadRequested)
            ? false
            : state.awaitingPaymentConfirmation,
      };

    case actionTypes.DISMISS_WELCOME_SUCCESS:
      return { ...state, welcomeSplashSeen: true };

    case actionTypes.UPLOAD_MANAGED_FILE_SUCCESS:
      return {
        ...state,
        managedUploadFileUploaded: true,
        managedUploadStatus: 'pending',
        managedUploadAdminMessage: null,
      };

    case actionTypes.SET_MANAGED_UPLOAD_ADMIN_MESSAGE:
      return { ...state, managedUploadAdminMessage: action.payload };

    case actionTypes.GET_USAGE_SUCCESS:
      return {
        ...state,
        contactCount: action.payload.current,
        contactLimit: action.payload.limit,
        recommendedTier: action.payload.recommended_tier,
      };

    case actionTypes.PURCHASE_SETUP_FEE_SUCCESS:
      return { ...state, managedUploadRequested: true, paymentProcessing: false, awaitingPaymentConfirmation: false };

    case actionTypes.CONFIRM_SETUP_PURCHASE_SUCCESS:
      return {
        ...state,
        managedUploadRequested: true,
        awaitingPaymentConfirmation: false,
        // Apply any managed upload state resets returned by the backend (e.g. after a repeat purchase
        // the backend resets file_uploaded=false and status=null so the upload area reappears).
        ...(action.payload?.managed_upload_file_uploaded !== undefined && {
          managedUploadFileUploaded: action.payload.managed_upload_file_uploaded,
        }),
        ...(action.payload?.managed_upload_status !== undefined && {
          managedUploadStatus: action.payload.managed_upload_status,
        }),
      };

    default:
      return state;
  }
};

export { billingActions, billingTriggerActions } from "../actions/creators/billingActions";
